import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TenantRoleWizardComponent } from '../../modals/tenant-role-wizard/tenant-role-wizard.component';

interface PageOperation {
  key: string;
  label: string;
}

interface PageDef {
  name: string;
  operations: PageOperation[];
}

interface ImmunityRole {
  id: string;
  name: string;
  pageNames: string[];
  operationsByPage: Record<string, string[]>;
}

const PAGE_CATALOG: PageDef[] = [
  { name: 'Dashboard', operations: [{ key: 'view', label: 'View' }] },
  { name: 'Tenants', operations: [{ key: 'view', label: 'View' }, { key: 'create', label: 'Create' }, { key: 'edit', label: 'Edit' }, { key: 'delete', label: 'Delete' }] },
  { name: 'Users', operations: [{ key: 'view', label: 'View' }, { key: 'create', label: 'Create' }, { key: 'edit', label: 'Edit' }, { key: 'delete', label: 'Delete' }, { key: 'approve', label: 'Approve' }] },
  { name: 'Roles', operations: [{ key: 'view', label: 'View' }, { key: 'edit', label: 'Edit' }] },
  { name: 'Settings', operations: [] }
];

// TODO: fetch from the API.
const INITIAL_ROLES: ImmunityRole[] = [
  { id: 'administrator', name: 'Administrator', pageNames: ['Dashboard', 'Tenants', 'Users', 'Roles'], operationsByPage: { Tenants: ['view', 'create', 'edit'], Users: ['view', 'edit', 'approve'], Roles: ['view', 'edit'] } },
  { id: 'viewer', name: 'Viewer', pageNames: ['Dashboard'], operationsByPage: {} },
  { id: 'unassigned', name: 'Unassigned', pageNames: [], operationsByPage: {} }
];

@Component({
  selector: 'app-tenant-role-immunity',
  imports: [FormsModule, TenantRoleWizardComponent],
  templateUrl: './tenant-role-immunity.component.html',
  styleUrl: './tenant-role-immunity.component.scss'
})
export class TenantRoleImmunityComponent implements OnInit {
  canEdit = true; // TODO: derive from the current user's own permissions.

  isLoading = true;
  roles: ImmunityRole[] = [];
  expandedRoleIds = new Set<string>();

  pageCatalog = PAGE_CATALOG;

  isOpen = false;
  step: 1 | 2 = 1;
  draft: { id: string | null; name: string; pageNames: string[] } = { id: null, name: '', pageNames: [] };

  pageOpsDraft: Record<string, string[]> = {};
  lastSavedOps: Record<string, string[]> = {};
  savingPage: string | null = null;
  savedFlash: Record<string, boolean> = {};

  ngOnInit(): void {
    setTimeout(() => {
      this.roles = INITIAL_ROLES;
      this.isLoading = false;
    }, 500);
  }

  visiblePages(role: ImmunityRole): string[] {
    const expanded = this.expandedRoleIds.has(role.id);
    return expanded ? role.pageNames : role.pageNames.slice(0, 3);
  }

  hiddenCount(role: ImmunityRole): number {
    return Math.max(0, role.pageNames.length - 3);
  }

  toggleExpanded(role: ImmunityRole): void {
    this.expandedRoleIds.has(role.id) ? this.expandedRoleIds.delete(role.id) : this.expandedRoleIds.add(role.id);
  }

  isExpanded(role: ImmunityRole): boolean {
    return this.expandedRoleIds.has(role.id);
  }

  openCreate(): void {
    this.draft = { id: null, name: '', pageNames: [] };
    this.step = 1;
    this.isOpen = true;
  }

  openEdit(role: ImmunityRole): void {
    this.draft = { id: role.id, name: role.name, pageNames: [...role.pageNames] };
    this.step = 1;
    this.isOpen = true;
  }

  closeWizard(): void {
    this.isOpen = false;
  }

  isPageChecked(name: string): boolean {
    return this.draft.pageNames.includes(name);
  }

  togglePage(name: string): void {
    this.draft.pageNames = this.isPageChecked(name)
      ? this.draft.pageNames.filter(p => p !== name)
      : [...this.draft.pageNames, name];
  }

  selectAllPages(): void {
    this.draft.pageNames = this.pageCatalog.map(p => p.name);
  }

  clearAllPages(): void {
    this.draft.pageNames = [];
  }

  get canProceedStep1(): boolean {
    return !!this.draft.name.trim() && this.draft.pageNames.length > 0;
  }

  proceedToStep2(): void {
    if (!this.canProceedStep1) return;

    let role = this.roles.find(r => r.id === this.draft.id);
    if (!role) {
      role = { id: this.draft.name.trim().toLowerCase().replace(/\s+/g, '-'), name: this.draft.name.trim(), pageNames: [], operationsByPage: {} };
      this.roles.push(role);
      this.draft.id = role.id;
    }
    role.name = this.draft.name.trim();
    role.pageNames = [...this.draft.pageNames];

    this.pageOpsDraft = {};
    this.lastSavedOps = {};
    for (const page of this.pageCatalog) {
      const saved = role.operationsByPage[page.name] ?? [];
      this.pageOpsDraft[page.name] = [...saved];
      this.lastSavedOps[page.name] = [...saved];
    }

    this.step = 2;
  }

  pagesWithOperations(): PageDef[] {
    return this.pageCatalog.filter(p => p.operations.length > 0);
  }

  isPageAssigned(name: string): boolean {
    return this.draft.pageNames.includes(name);
  }

  isOpSelected(page: string, key: string): boolean {
    return (this.pageOpsDraft[page] ?? []).includes(key);
  }

  toggleOp(page: string, key: string): void {
    if (!this.isPageAssigned(page)) return;
    const current = this.pageOpsDraft[page] ?? [];
    this.pageOpsDraft[page] = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    this.savedFlash[page] = false;
  }

  isPagePending(page: string): boolean {
    const draft = [...(this.pageOpsDraft[page] ?? [])].sort();
    const saved = [...(this.lastSavedOps[page] ?? [])].sort();
    return JSON.stringify(draft) !== JSON.stringify(saved);
  }

  savePageOps(page: string): void {
    if (!this.isPagePending(page) || this.savingPage) return;
    this.savingPage = page;
    setTimeout(() => {
      const role = this.roles.find(r => r.id === this.draft.id);
      if (role) role.operationsByPage[page] = [...this.pageOpsDraft[page]];
      this.lastSavedOps[page] = [...this.pageOpsDraft[page]];
      this.savingPage = null;
      this.savedFlash[page] = true;
      setTimeout(() => (this.savedFlash[page] = false), 1500);
    }, 500);
  }
}
